import { useState } from "react";
import { LoadingSkeleton, useToast, ConfirmDialog, ModalSheet } from "@a2z/ui";
import { ErrorState } from "@/shared/components/ErrorState";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SellerOrderDetail } from "@/lib/api/types";
import { useOrderByIdQuery } from "@/modules/orders/services/useOrderByIdQuery";
import { useOrderInvoiceDownload } from "@/modules/orders/services/useOrderInvoiceDownload";
import { isInvoiceAvailable } from "@/modules/orders/lib/invoice";
import { PaymentReviewDialog } from "./PaymentReviewDialog";
import { OrderBody } from "./order-detail-modal/OrderBody";
import { useOrderActions } from "./order-detail-modal/useOrderActions";
import { OrderActionConfirm } from "./OrderActionConfirm";
import { ShipOrderSheet } from "./order-detail-modal/ShipOrderSheet";
import { CancelOrderSheet } from "./order-detail-modal/CancelOrderSheet";
import { DeclineOrderSheet } from "./order-detail-modal/DeclineOrderSheet";
import { OrderUndoControl } from "./order-detail-modal/OrderUndoControl";
import { OrderStatusBadge } from "./order-row/order-visuals";

type Props = {
  storeSlug: string;
  orderId: string | null;
  onClose: () => void;
};

/**
 * OrderDetailModal — Trioty order-detail drawer. A side-anchored `ModalSheet`
 * panel (bottom sheet on mobile) with a custom header (code + status pill +
 * date · items), a scrollable body card (see OrderBody), and a footer of
 * print · secondary (decline/cancel/discard) · primary CTA. All action wiring
 * — quick actions, payment review, decline, cancel, discard, undo, invoice —
 * is preserved from the previous modal.
 */
export function OrderDetailModal({ storeSlug, orderId, onClose }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const isOpen = orderId !== null;
  // When the payment review opens, hide the drawer so the review *replaces* it.
  const [reviewOpen, setReviewOpen] = useState(false);

  const {
    data: order,
    isLoading,
    error: orderError,
    refetch: refetchOrder,
  } = useOrderByIdQuery({ storeSlug, orderId: orderId ?? "" });
  const canReview =
    order?.status === "pending_payment_verification" && storeSlug.length > 0;

  return (
    <>
      <ModalSheet
        isOpen={isOpen && !reviewOpen}
        onClose={onClose}
        size="md"
        variant="drawer"
        surface="content"
      >
        {/* ── Header ── */}
        <header className="flex shrink-0 items-start justify-between gap-3 px-[18px] pb-3 pt-4">
          {order ? (
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span dir="ltr" className="truncate text-[18px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                  #{order.orderCode}
                </span>
                <OrderStatusBadge status={order.status} />
              </div>
              <div className="mt-1 text-[12.5px] text-[color:var(--text-muted)]">
                {formatDate(order.createdAt, locale)} ·{" "}
                {t(keys.orders.itemsCountSummary, { count: order.itemCount })}
              </div>
            </div>
          ) : (
            <span className="text-[15px] font-semibold text-[color:var(--text-primary)]">
              {t(keys.orders.drawerOrderFallback)}
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t(keys.common.close)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--card)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--text-primary)] lg:h-8 lg:w-8"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* ── Body card: scrollable content + attached footer ── */}
        <div className="flex min-h-0 flex-1 flex-col px-3.5 pb-3.5">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[16px] border border-[color:var(--card-br)] bg-[color:var(--card)]">
            <div className="min-h-0 flex-1 overflow-y-auto">
              {orderError && !isLoading ? (
                <div className="p-4">
                  <ErrorState
                    message={t(keys.orders.fetchTimeoutFallback)}
                    onRetry={() => {
                      void refetchOrder();
                    }}
                  />
                </div>
              ) : isLoading || !order ? (
                <div className="p-4">
                  <LoadingSkeleton variant="card" rows={5} />
                </div>
              ) : (
                <OrderBody order={order} storeSlug={storeSlug} />
              )}
            </div>
            {order ? (
              <DetailFooter
                order={order}
                storeSlug={storeSlug}
                onReviewPayment={canReview ? () => setReviewOpen(true) : undefined}
                onClose={onClose}
              />
            ) : null}
          </div>
        </div>
      </ModalSheet>

      {order && canReview ? (
        <PaymentReviewDialog
          locale={locale}
          storeSlug={storeSlug}
          orderId={order.id}
          isOpen={reviewOpen}
          onClose={() => setReviewOpen(false)}
          onSuccess={() => {
            setReviewOpen(false);
            toast.success(t(keys.orders.statusSavedMicro));
          }}
        />
      ) : null}
    </>
  );
}

/* ── Footer: print · secondary (decline/cancel/discard) · primary CTA ────── */
type FooterProps = {
  order: SellerOrderDetail;
  storeSlug: string;
  onReviewPayment?: () => void;
  onClose: () => void;
};

function DetailFooter({ order, storeSlug, onReviewPayment, onClose }: FooterProps) {
  const t = useTranslations();
  const [declineOpen, setDeclineOpen] = useState(false);
  const { download, isLoading: invoiceLoading } = useOrderInvoiceDownload();
  const actions = useOrderActions({ order, storeSlug, onReviewPayment, onClose });
  const {
    primary,
    primaryConfirm,
    secondary,
    dialogState,
    isSaving,
    isDraft,
    isDraftSaving,
    submitDeclineWith,
  } = actions;
  const canCancel = secondary.some((a) => a.key === "cancel");
  const canDecline = secondary.some((a) => a.key === "decline");
  const invoiceAvail = isInvoiceAvailable(order.paymentMethod, order.status);
  // The forward action into `shipped` is the "Ship" chooser (carriers or
  // manual), not the plain confirm popup every other transition uses.
  const isShipAction = primaryConfirm?.toStatus === "shipped";

  const dangerBtn =
    "inline-flex h-[42px] min-w-[120px] flex-1 items-center justify-center gap-2 rounded-[11px] border border-[color:var(--border-strong)] bg-[color:var(--card)] px-[11px] text-[13.5px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--soft)] disabled:opacity-50";

  return (
    <>
      {/* Action dialogs. The `shipped` transition uses the carrier chooser
          (ShipOrderSheet) instead of the plain confirm popup. */}
      {primaryConfirm && !isShipAction ? (
        <OrderActionConfirm
          isOpen={primaryConfirm.open}
          orderCode={order.orderCode}
          fromStatus={primaryConfirm.fromStatus}
          toStatus={primaryConfirm.toStatus}
          bodyKey={primaryConfirm.needsEta ? null : primaryConfirm.copy.bodyKey}
          confirmKey={primaryConfirm.copy.confirmKey}
          needsEta={primaryConfirm.needsEta}
          isSubmitting={isSaving}
          onConfirm={(eta) => void primaryConfirm.run(eta)}
          onClose={() => primaryConfirm.setOpen(false)}
        />
      ) : null}

      {/* Ship chooser (carriers or manual) for the `shipped` transition. */}
      {primaryConfirm && isShipAction ? (
        <ShipOrderSheet
          storeSlug={storeSlug}
          orderId={order.id}
          isOpen={primaryConfirm.open}
          onClose={() => primaryConfirm.setOpen(false)}
          onShipManually={() => primaryConfirm.run(null)}
          isShippingManually={isSaving}
        />
      ) : null}

      <DeclineOrderSheet
        isOpen={declineOpen}
        onClose={() => setDeclineOpen(false)}
        onConfirm={(reasonCode) => {
          void submitDeclineWith(reasonCode);
          setDeclineOpen(false);
        }}
        isSubmitting={isSaving}
      />
      <CancelOrderSheet
        isOpen={dialogState.cancelOpen}
        onClose={() => dialogState.setCancelOpen(false)}
        order={order}
        onConfirm={(reasonCode, note) =>
          void dialogState.onConfirmCancel(reasonCode, note)
        }
        isSubmitting={isSaving}
      />
      <ConfirmDialog
        isOpen={dialogState.discardOpen}
        title={t(keys.orders.discardDraftConfirmTitle)}
        body={t(keys.orders.discardDraftConfirmBody)}
        confirmLabel={t(keys.orders.discardDraftAction)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isDraftSaving}
        onConfirm={() => void dialogState.onConfirmDiscard()}
        onCancel={() => dialogState.setDiscardOpen(false)}
      />

      <footer className="flex shrink-0 flex-wrap items-center gap-[9px] border-t border-[color:var(--line)] px-3.5 py-3">
        <OrderUndoControl order={order} storeSlug={storeSlug} />
        {invoiceAvail ? (
          <button
            type="button"
            onClick={() => void download(order.id)}
            disabled={invoiceLoading}
            aria-label={t(keys.orders.downloadInvoice)}
            title={t(keys.orders.downloadInvoice)}
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)] disabled:opacity-50"
          >
            {invoiceLoading ? (
              <svg aria-hidden="true" className="h-[17px] w-[17px] animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 9V4h12v5" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" rx="1" />
              </svg>
            )}
          </button>
        ) : null}
        {isDraft ? (
          <button
            type="button"
            onClick={() => dialogState.setDiscardOpen(true)}
            className={dangerBtn}
          >
            {t(keys.orders.discardDraftAction)}
          </button>
        ) : (
          <>
            {canDecline ? (
              <button
                type="button"
                onClick={() => setDeclineOpen(true)}
                className={dangerBtn}
              >
                {t(keys.orders.actionDeclineTitle)}
              </button>
            ) : null}
            {canCancel ? (
              <button
                type="button"
                onClick={() => dialogState.setCancelOpen(true)}
                className={dangerBtn}
              >
                {t(keys.orders.actionCancelTitle)}
              </button>
            ) : null}
          </>
        )}
        {primary ? (
          <button
            type="button"
            onClick={() => void primary.run()}
            disabled={primary.disabled}
            className="inline-flex h-[42px] min-w-[150px] flex-[1.4] items-center justify-center gap-2 rounded-[11px] bg-[color:var(--accent-500)] px-[11px] text-[13.5px] font-bold text-[color:var(--on-accent)] transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12l5 5L20 7" />
            </svg>
            {t(primary.labelKey)}
          </button>
        ) : null}
      </footer>
    </>
  );
}

function formatDate(iso: string, locale: string = "en"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}
